import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeft, Loader2, Building2, FileText, Landmark, ShieldAlert, BadgeCheck,
  Mail, Phone, ChevronRight, Save, AlertTriangle, Plus, Power, MapPin, UserCircle, StickyNote, Trash2,
} from 'lucide-react';
import { INV_STATUS, statusLabel } from './pmStatus';
import AddressAutocomplete from '../../components/AddressAutocomplete';

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => { if (!d) return '—'; try { return format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy'); } catch { return String(d); } };
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const KPI = ({ label, value, from, to }) => (
  <div className={clsx('relative px-4 py-4 text-white overflow-hidden bg-gradient-to-br', from, to)}>
    <div className="text-[11px] font-medium opacity-80">{label}</div>
    <div className="text-lg font-bold tabular-nums mt-1 leading-tight">{value}</div>
    <div className="absolute -right-3 -bottom-3 w-16 h-16 rounded-full bg-white/10" />
  </div>
);

const FieldRow = ({ label, children, hint, required }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}{required && <span className="text-rose-500"> *</span>}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="text-[11px] text-slate-400 mt-0.5 block">{hint}</span>}
  </label>
);

export default function ProviderProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [data, setData] = useState(isNew ? { provider: {}, kpis: {}, invoices: [] } : undefined);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [abnBusy, setAbnBusy] = useState(false);

  // What the ABN field says underneath. Three spellings of "good" already exist in the data
  // ('verified' written here, 'valid' from the demo seed, 'active' in Reports), so all three read
  // as verified rather than one of them silently blocking the provider.
  const abnHint = (f) => {
    const st = String(f.abn_status || '').toLowerCase();
    if (['REIMB', 'EXCLS'].includes(String(f.abn_exemption || '').toUpperCase())) return `Exempt (${f.abn_exemption}) — no ABN needed`;
    if (st === 'invalid') return 'Fails the ATO checksum — check for a typo';
    if (['verified', 'valid', 'active'].includes(st)) {
      return f.abn_verified_at ? `Checked ${String(f.abn_verified_at).slice(0, 10)}` : 'Checked';
    }
    return 'Never checked — claiming needs this';
  };

  // Run the check the invoice block asks for, then reload so the hint and the provider status
  // reflect it. The server re-validates every invoice this provider was blocking.
  const verifyAbn = async () => {
    if (isNew) { toast.error('Save the provider first, then check the ABN.'); return; }
    setAbnBusy(true);
    try {
      const { data: r } = await api.post(`/plan-management/providers/${id}/verify-abn`, {});
      toast.success(r.message || 'ABN checked');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not check that ABN');
      load();
    } finally { setAbnBusy(false); }
  };

  const load = useCallback(() => {
    if (isNew) { setData({ provider: {}, kpis: {}, invoices: [] }); setForm({}); return; }
    setData(undefined);
    api.get(`/plan-management/providers/${id}/profile`)
      .then((r) => { setData(r.data); setForm(r.data?.provider || {}); })
      .catch(() => setData(null));
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);

  // Provider portal access (standalone /provider-portal login — payment self-service).
  const [portalAccounts, setPortalAccounts] = useState([]);
  const [invite, setInvite] = useState({ name: '', email: '' });
  const [portalBusy, setPortalBusy] = useState('');
  const loadPortal = useCallback(() => {
    if (isNew) return;
    api.get('/provider-portal/accounts', { params: { provider_id: id } }).then((r) => setPortalAccounts(r.data || [])).catch(() => {});
  }, [id, isNew]);
  useEffect(() => { loadPortal(); }, [loadPortal]);
  const sendInvite = async () => {
    if (!invite.email.trim()) { toast.error('Enter an email address'); return; }
    setPortalBusy('invite');
    try {
      const r = await api.post('/provider-portal/accounts', { provider_id: id, name: invite.name.trim(), email: invite.email.trim() });
      toast.success(r.data.emailed ? 'Invite emailed' : 'Account created — email failed');
      setInvite({ name: '', email: '' }); loadPortal();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setPortalBusy(''); }
  };
  const resendInvite = async (aid) => {
    setPortalBusy('r' + aid);
    try { const r = await api.post(`/provider-portal/accounts/${aid}/resend`, {}); toast.success(r.data.emailed ? 'Invite re-sent' : 'New link generated — email failed'); loadPortal(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setPortalBusy(''); }
  };
  const revokeAccess = async (aid) => {
    setPortalBusy('x' + aid);
    try { await api.delete(`/provider-portal/accounts/${aid}`); toast.success('Access revoked'); loadPortal(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setPortalBusy(''); }
  };

  const set = (kk, v) => setForm((f) => ({ ...f, [kk]: v }));
  const save = async () => {
    if (!form.name || !form.name.trim()) { toast.error('Provider name is required'); return; }
    setBusy(true);
    const payload = {
      name: form.name.trim(), abn: form.abn || null, ndis_reg_number: form.ndis_reg_number || null,
      email: form.email || null, phone: form.phone || null, address: form.address || null, contact_person: form.contact_person || null,
      bank_bsb: form.bank_bsb || null, bank_account: form.bank_account || null, bank_account_name: form.bank_account_name || null,
      exclude_from_aba: !!form.exclude_from_aba, related_party: !!form.related_party, related_party_reason: form.related_party_reason || null,
      notes: form.notes || null,
    };
    try {
      if (isNew) {
        const r = await api.post('/service-providers', payload);
        toast.success('Provider created');
        navigate(`/plan-management/providers/${r.data.id}`, { replace: true });
      } else {
        await api.put(`/service-providers/${id}`, payload);
        toast.success('Provider saved');
        load();
      }
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); } finally { setBusy(false); }
  };
  const toggleActive = async () => {
    if (isNew) return;
    try { await api.put(`/service-providers/${id}`, { is_active: !(data?.provider?.is_active !== false) }); toast.success(data?.provider?.is_active === false ? 'Provider reactivated' : 'Provider deactivated'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  // Hard delete. The API refuses as soon as the provider has an invoice or appears in a payment
  // batch, and its 409 message names the alternative — so it is surfaced verbatim rather than
  // flattened into a generic failure.
  const deleteProvider = async () => {
    if (isNew) return;
    const nm = data?.provider?.name || 'this provider';
    if (!window.confirm(`Permanently delete ${nm}? This is only possible while they have no invoices — mark them inactive instead if you want to keep their history.`)) return;
    try {
      await api.delete(`/plan-management/providers/${id}`);
      toast.success('Provider deleted');
      navigate('/plan-management?view=providers');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not delete'); }
  };
  const openInvoice = (invId) => navigate(`/plan-management?openInvoice=${invId}`);

  if (data === undefined) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={28} /></div>;
  if (!data) return (
    <div className="p-6 max-w-3xl mx-auto">
      <button onClick={() => navigate('/plan-management')} className="text-sm text-primary-600 flex items-center gap-1.5 mb-4"><ArrowLeft size={15} /> Back to Plan Management</button>
      <div className="rounded-2xl border border-rose-100 bg-rose-50 p-8 text-center text-rose-700">Could not load this provider profile.</div>
    </div>
  );

  const p = data.provider || {};
  const k = data.kpis || {};
  const I = 'input-field w-full text-sm';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="w-full px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/plan-management')} className="text-slate-500 hover:text-slate-800 flex items-center gap-1.5 text-sm font-medium"><ArrowLeft size={16} /> Plan Management</button>
          <ChevronRight size={14} className="text-slate-300" />
          <span className="text-sm font-semibold text-slate-800 truncate">{isNew ? (form.name || 'New provider') : p.name}</span>
          {!isNew && (form.ndis_reg_number
            ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">Registered</span>
            : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">Unregistered</span>)}
          {!isNew && form.related_party && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 flex items-center gap-1"><ShieldAlert size={11} /> Related party</span>}
          {!isNew && p.is_active === false && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Inactive</span>}
          <span className="flex-1" />
          {!isNew && <button onClick={toggleActive} className={clsx('!text-xs !px-3 !py-1.5 rounded-lg font-medium border flex items-center gap-1.5', p.is_active === false ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50' : 'border-slate-200 text-slate-600 hover:bg-rose-50 hover:text-rose-600')}><Power size={13} /> {p.is_active === false ? 'Reactivate' : 'Deactivate'}</button>}
          {!isNew && <button onClick={deleteProvider} title="Only possible while this provider has no invoices" className="!text-xs !px-3 !py-1.5 rounded-lg font-medium border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 flex items-center gap-1.5"><Trash2 size={13} /> Delete</button>}
          <button onClick={save} disabled={busy} className="btn-primary !text-xs !px-4 !py-1.5 flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : isNew ? <Plus size={13} /> : <Save size={13} />} {isNew ? 'Create provider' : 'Save'}</button>
        </div>
      </div>

      <div className="w-full px-6 py-6 space-y-6">
        {isNew && (
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800 flex items-center gap-2">
            <Plus size={16} className="shrink-0" /> Creating a new service-provider profile. Fill in the details and click <strong>Create provider</strong> — you'll land on their full profile with invoice history.
          </div>
        )}

        {/* KPI strip (existing providers only) */}
        {!isNew && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <KPI label="Total Invoiced" value={money(k.total)} from="from-sky-500" to="to-sky-600" />
            <KPI label="Total Paid" value={money(k.paid)} from="from-emerald-500" to="to-emerald-600" />
            <KPI label="Outstanding" value={money(k.outstanding)} from="from-amber-500" to="to-amber-600" />
            <KPI label="Invoices" value={k.invoices ?? 0} from="from-indigo-500" to="to-indigo-600" />
          </div>
        )}

        {/* COI banner */}
        {!isNew && form.related_party && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2">
            <AlertTriangle size={16} className="shrink-0" /> This provider is flagged as a <strong>related party</strong> — invoices trigger a conflict-of-interest gate before approval, and self-payments are split out of the ABA bank run.
          </div>
        )}

        {/* Bank-change verification hold (invoice-redirection fraud guard) */}
        {!isNew && p.bank_verified === false && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex items-center gap-3 flex-wrap">
            <AlertTriangle size={16} className="shrink-0" />
            <span className="flex-1 min-w-[240px]">
              <strong>Bank details changed{p.bank_updated_at ? ` on ${fmtD(p.bank_updated_at)}` : ''} — payments are on hold.</strong>{' '}
              Call the provider on the number already on file (not one from the email that requested the change) to confirm the new account, then mark it verified. ABA runs skip this provider until then.
            </span>
            <button
              onClick={async () => { try { await api.patch(`/plan-management/providers/${id}/verify-bank`, {}); toast.success('Bank details verified — payments released'); load(); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } }}
              className="!text-xs !px-3 !py-1.5 rounded-lg font-semibold bg-rose-600 text-white hover:bg-rose-700 flex items-center gap-1.5"
            >
              <BadgeCheck size={13} /> I've verified — release payments
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6">
          {/* Identity + registration */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center shadow-sm"><Building2 size={26} /></div>
              <div className="min-w-0 flex-1">
                <input className={`${I} font-semibold`} value={form.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="Provider name *" />
              </div>
            </div>
            <h3 className="text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5"><BadgeCheck size={13} /> Identity & registration</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* The "Provider verified" block on an invoice asks the operator to run the ABN check.
                  There was no ABN check: nothing in the app ever wrote abn_status, so it sat at
                  'unverified' forever and the block could not be cleared. This is that check. */}
              <FieldRow label="ABN" hint={abnHint(form)}>
                <div className="flex items-center gap-2">
                  <input className={I} value={form.abn || ''} onChange={(e) => set('abn', e.target.value)} />
                  <button type="button" onClick={verifyAbn} disabled={abnBusy}
                    title="Check this ABN against the ATO checksum"
                    className="shrink-0 px-2.5 py-1.5 text-[12px] font-semibold rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50">
                    {abnBusy ? 'Checking…' : 'Check ABN'}
                  </button>
                </div>
              </FieldRow>
              <FieldRow label="NDIS Registration #" hint="Blank = unregistered (still payable by PM)"><input className={I} value={form.ndis_reg_number || ''} onChange={(e) => set('ndis_reg_number', e.target.value)} /></FieldRow>
              <FieldRow label="Email"><div className="relative"><Mail size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" /><input className={`${I} !pl-8`} value={form.email || ''} onChange={(e) => set('email', e.target.value)} placeholder="provider@example.com" /></div></FieldRow>
              <FieldRow label="Phone"><div className="relative"><Phone size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" /><input className={`${I} !pl-8`} value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="04xx xxx xxx" /></div></FieldRow>
              <FieldRow label="Contact person"><div className="relative"><UserCircle size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" /><input className={`${I} !pl-8`} value={form.contact_person || ''} onChange={(e) => set('contact_person', e.target.value)} /></div></FieldRow>
              <FieldRow label="Address"><AddressAutocomplete className={I} value={form.address || ''} onChange={v => set('address', v)} onSelect={a => set('address', [a.street, a.suburb, a.state, a.postcode].filter(Boolean).join(', '))} /></FieldRow>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.related_party} onChange={(e) => set('related_party', e.target.checked)} />
              Related party (conflict-of-interest gate)
            </label>
            {form.related_party && <FieldRow label="COI reason / relationship"><input className={I} value={form.related_party_reason || ''} onChange={(e) => set('related_party_reason', e.target.value)} placeholder="e.g. same director / owner" /></FieldRow>}
            <FieldRow label="Notes"><textarea rows={2} className={I} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} /></FieldRow>
          </div>

          {/* Banking for ABA */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
            <h3 className="text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5"><Landmark size={13} /> Banking — ABA payment file</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="BSB"><input className={I} value={form.bank_bsb || ''} onChange={(e) => set('bank_bsb', e.target.value)} placeholder="000-000" /></FieldRow>
              <FieldRow label="Account number"><input className={I} value={form.bank_account || ''} onChange={(e) => set('bank_account', e.target.value)} /></FieldRow>
              <FieldRow label="Account name"><input className={I} value={form.bank_account_name || ''} onChange={(e) => set('bank_account_name', e.target.value)} /></FieldRow>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.exclude_from_aba} onChange={(e) => set('exclude_from_aba', e.target.checked)} />
              Exclude from ABA file <span className="text-xs text-slate-400">(pay this provider manually)</span>
            </label>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-[11px] text-slate-500 leading-relaxed">
              Bank details feed the generated <strong>ABA</strong> bank file used to pay providers. Plan management pays <strong>registered and unregistered</strong> providers alike — NDIS registration is for reporting only.
            </div>
          </div>
        </div>

        {/* Provider portal access — the standalone /provider-portal login (payment self-service) */}
        {!isNew && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><UserCircle size={15} className="text-teal-600" /> Provider portal access</h3>
              <span className="text-[11px] text-slate-400">They sign in at <span className="font-mono text-slate-500">/provider-portal</span> to check invoice &amp; payment status — fewer "where's my payment?" calls</span>
            </div>
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <div><label className="block text-[11px] font-medium text-slate-500 mb-1">Name</label><input className="input-field !text-xs !py-1.5 !w-44" placeholder="Accounts contact" value={invite.name} onChange={(e) => setInvite((p) => ({ ...p, name: e.target.value }))} /></div>
              <div><label className="block text-[11px] font-medium text-slate-500 mb-1">Email</label><input className="input-field !text-xs !py-1.5 !w-60" placeholder="accounts@provider.com.au" value={invite.email} onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))} /></div>
              <button onClick={sendInvite} disabled={portalBusy === 'invite'} className="btn-primary !text-xs !px-3 !py-2 flex items-center gap-1.5 disabled:opacity-50">{portalBusy === 'invite' ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Send invite</button>
            </div>
            {portalAccounts.length === 0 ? (
              <p className="text-xs text-slate-400">No portal access yet — invite the provider's accounts contact above.</p>
            ) : (
              <div className="divide-y divide-slate-50">
                {portalAccounts.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <span className="text-sm text-slate-700 font-medium">{a.name || a.email}</span>
                      <span className="text-[11px] text-slate-400 block">{a.email}{a.last_login ? ` · last sign-in ${fmtD(a.last_login)}` : ''}</span>
                    </div>
                    <span className={clsx('badge !text-[10px]', a.status === 'active' ? 'badge-green' : a.status === 'revoked' ? 'badge-red' : 'badge-yellow')}>{cap(a.status)}</span>
                    {a.status !== 'revoked' && <button onClick={() => resendInvite(a.id)} disabled={!!portalBusy} className="text-[11px] text-primary-600 hover:underline disabled:opacity-40">Resend invite</button>}
                    {a.status !== 'revoked' && <button onClick={() => revokeAccess(a.id)} disabled={!!portalBusy} title="Revoke access" className="text-slate-300 hover:text-rose-600 disabled:opacity-40">✕</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invoice history (existing providers only) */}
        {!isNew && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-3"><FileText size={15} className="text-primary-600" /> Invoice history <span className="text-slate-400 font-normal">({data.invoices?.length || 0})</span></h3>
            {(data.invoices?.length || 0) === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No invoices from this provider yet.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400"><tr><th className="px-2.5 py-2 text-left font-semibold">Date</th><th className="px-2.5 py-2 text-left font-semibold">Invoice #</th><th className="px-2.5 py-2 text-left font-semibold">Participant</th><th className="px-2.5 py-2 text-right font-semibold">Amount</th><th className="px-2.5 py-2 text-left font-semibold">Status</th></tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {data.invoices.map((v) => (
                      <tr key={v.id} className="hover:bg-primary-50/40 cursor-pointer" onClick={() => openInvoice(v.id)}>
                        <td className="px-2.5 py-2 text-slate-600">{fmtD(v.invoice_date)}</td>
                        <td className="px-2.5 py-2 font-mono text-slate-500">{v.provider_invoice_number || '—'}</td>
                        <td className="px-2.5 py-2 text-slate-700">{v.client_name}</td>
                        <td className="px-2.5 py-2 text-right tabular-nums font-semibold">{money(v.total_amount)}</td>
                        <td className="px-2.5 py-2"><span className={clsx('badge !text-[10px]', INV_STATUS[v.status] || 'badge-gray')}>{statusLabel(v.status)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
